/**
 * User preferences store
 * Manages theme, layout preferences, and user settings
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { STORAGE_KEYS } from '@/utils/constants';

/**
 * Table column visibility preferences
 */
interface ColumnPreferences {
  jobs: string[];
  places: string[];
}

/**
 * Theme type
 */
type Theme = 'light' | 'dark';

/**
 * Preferences state interface
 */
interface PreferencesState {
  // Theme
  isDarkMode: boolean;
  theme: Theme; // Derived getter for component compatibility

  // Layout
  sidebarCollapsed: boolean;

  // Table preferences
  pageSize: number;
  columns: ColumnPreferences;

  // Scraping preferences
  defaultZoomLevel: number;
  defaultOverlapPercent: number;

  // Notifications
  soundEnabled: boolean;
  desktopNotifications: boolean;

  // Recent searches
  recentSearches: string[];
  maxRecentSearches: number;

  // Actions
  toggleTheme: () => void;
  setDarkMode: (isDark: boolean) => void;
  setTheme: (theme: Theme) => void;
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  setPageSize: (size: number) => void;
  setJobColumns: (columns: string[]) => void;
  setPlaceColumns: (columns: string[]) => void;
  setDefaultZoomLevel: (zoom: number) => void;
  setDefaultOverlapPercent: (overlap: number) => void;
  setSoundEnabled: (enabled: boolean) => void;
  setDesktopNotifications: (enabled: boolean) => void;
  addRecentSearch: (query: string) => void;
  clearRecentSearches: () => void;
  resetPreferences: () => void;
}

/**
 * Default column configurations
 */
const defaultJobColumns = [
  'searchQuery',
  'status',
  'progress',
  'totalPlaces',
  'createdAt',
];

const defaultPlaceColumns = [
  'name',
  'category',
  'address',
  'phone',
  'website',
  'rating',
  'reviewCount',
];

/**
 * Initial state (Dark mode is the default)
 */
const initialState = {
  isDarkMode: true,
  theme: 'dark' as Theme,
  sidebarCollapsed: false,
  pageSize: 50,
  columns: {
    jobs: defaultJobColumns,
    places: defaultPlaceColumns,
  },
  defaultZoomLevel: 16,
  defaultOverlapPercent: 10,
  soundEnabled: true,
  desktopNotifications: false,
  recentSearches: [],
  maxRecentSearches: 10,
};

/**
 * Preferences store with persistence
 */
export const usePreferencesStore = create<PreferencesState>()(
  persist(
    (set, get) => ({
      ...initialState,

      toggleTheme: () => {
        const newIsDark = !get().isDarkMode;
        const newTheme: Theme = newIsDark ? 'dark' : 'light';
        set({ isDarkMode: newIsDark, theme: newTheme });

        // Update document class for CSS variables
        if (newIsDark) {
          document.documentElement.classList.add('dark');
          document.documentElement.setAttribute('data-theme', 'dark');
        } else {
          document.documentElement.classList.remove('dark');
          document.documentElement.setAttribute('data-theme', 'light');
        }
      },

      setDarkMode: (isDark: boolean) => {
        const newTheme: Theme = isDark ? 'dark' : 'light';
        set({ isDarkMode: isDark, theme: newTheme });

        if (isDark) {
          document.documentElement.classList.add('dark');
          document.documentElement.setAttribute('data-theme', 'dark');
        } else {
          document.documentElement.classList.remove('dark');
          document.documentElement.setAttribute('data-theme', 'light');
        }
      },

      setTheme: (theme: Theme) => {
        const isDark = theme === 'dark';
        set({ isDarkMode: isDark, theme });

        if (isDark) {
          document.documentElement.classList.add('dark');
          document.documentElement.setAttribute('data-theme', 'dark');
        } else {
          document.documentElement.classList.remove('dark');
          document.documentElement.setAttribute('data-theme', 'light');
        }
      },

      toggleSidebar: () => {
        set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed }));
      },

      setSidebarCollapsed: (collapsed: boolean) => {
        set({ sidebarCollapsed: collapsed });
      },

      setPageSize: (size: number) => {
        set({ pageSize: size });
      },

      setJobColumns: (columns: string[]) => {
        set((state) => ({
          columns: { ...state.columns, jobs: columns },
        }));
      },

      setPlaceColumns: (columns: string[]) => {
        set((state) => ({
          columns: { ...state.columns, places: columns },
        }));
      },

      setDefaultZoomLevel: (zoom: number) => {
        set({ defaultZoomLevel: Math.max(14, Math.min(18, zoom)) });
      },

      setDefaultOverlapPercent: (overlap: number) => {
        set({ defaultOverlapPercent: Math.max(0, Math.min(50, overlap)) });
      },

      setSoundEnabled: (enabled: boolean) => {
        set({ soundEnabled: enabled });
      },

      setDesktopNotifications: (enabled: boolean) => {
        set({ desktopNotifications: enabled });

        // Request permission if enabling
        if (enabled && 'Notification' in window) {
          Notification.requestPermission();
        }
      },

      addRecentSearch: (query: string) => {
        const trimmedQuery = query.trim();
        if (!trimmedQuery) return;

        set((state) => {
          // Remove duplicate if exists
          const filtered = state.recentSearches.filter(
            (s) => s.toLowerCase() !== trimmedQuery.toLowerCase()
          );

          // Add to front and limit
          const updated = [trimmedQuery, ...filtered].slice(
            0,
            state.maxRecentSearches
          );

          return { recentSearches: updated };
        });
      },

      clearRecentSearches: () => {
        set({ recentSearches: [] });
      },

      resetPreferences: () => {
        set(initialState);
        document.documentElement.classList.add('dark');
        document.documentElement.setAttribute('data-theme', 'dark');
      },
    }),
    {
      name: STORAGE_KEYS.PREFERENCES,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        isDarkMode: state.isDarkMode,
        theme: state.theme,
        sidebarCollapsed: state.sidebarCollapsed,
        pageSize: state.pageSize,
        columns: state.columns,
        defaultZoomLevel: state.defaultZoomLevel,
        defaultOverlapPercent: state.defaultOverlapPercent,
        soundEnabled: state.soundEnabled,
        desktopNotifications: state.desktopNotifications,
        recentSearches: state.recentSearches,
      }),
      onRehydrateStorage: () => (state) => {
        // Apply theme on rehydration (dark mode is default)
        const isDark = state?.isDarkMode ?? true;
        if (isDark) {
          document.documentElement.classList.add('dark');
          document.documentElement.setAttribute('data-theme', 'dark');
        } else {
          document.documentElement.classList.remove('dark');
          document.documentElement.setAttribute('data-theme', 'light');
        }
      },
    }
  )
);

/**
 * Hook to get theme state
 */
export function useTheme() {
  const isDarkMode = usePreferencesStore((state) => state.isDarkMode);
  const theme = usePreferencesStore((state) => state.theme);
  const toggleTheme = usePreferencesStore((state) => state.toggleTheme);
  const setDarkMode = usePreferencesStore((state) => state.setDarkMode);
  const setTheme = usePreferencesStore((state) => state.setTheme);

  return { isDarkMode, theme, toggleTheme, setDarkMode, setTheme };
}

/**
 * Hook to get sidebar state
 */
export function useSidebar() {
  const collapsed = usePreferencesStore((state) => state.sidebarCollapsed);
  const toggle = usePreferencesStore((state) => state.toggleSidebar);
  const setCollapsed = usePreferencesStore((state) => state.setSidebarCollapsed);

  return { collapsed, toggle, setCollapsed };
}

/**
 * Hook to get recent searches
 */
export function useRecentSearches() {
  const searches = usePreferencesStore((state) => state.recentSearches);
  const addSearch = usePreferencesStore((state) => state.addRecentSearch);
  const clearSearches = usePreferencesStore((state) => state.clearRecentSearches);

  return { searches, addSearch, clearSearches };
}
